/**
 * PWA Install Prompt Component
 * Shows custom install banner for Android and desktop browsers
 */

import React, { useState, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import './InstallPrompt.css';
import { trackEvent } from '../../utils/analytics';

const InstallPrompt = () => {
  const { t } = useTranslation('mobile', { useSuspense: false });
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [showIOSGuide, setShowIOSGuide] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      console.log('[InstallPrompt] Already installed as PWA');
      return;
    }

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if prompt was dismissed recently (within 24 hours)
    const dismissedAt = localStorage.getItem('pwa_prompt_dismissed');
    if (dismissedAt) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedAt)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        console.log('[InstallPrompt] Prompt recently dismissed, showing again in', Math.ceil(24 - hoursSinceDismissed), 'hours');
        return;
      }
    }

    // For iOS, show after delay
    if (iOS) {
      setTimeout(() => setShowPrompt(true), 3000);
      return;
    }

    // For Android/Desktop - listen for beforeinstallprompt
    const handleBeforeInstallPrompt = (e) => {
      console.log('[InstallPrompt] beforeinstallprompt event fired');

      // Prevent default mini-infobar
      e.preventDefault();

      // Store event for later use
      setDeferredPrompt(e);

      // Show custom prompt after delay
      setTimeout(() => setShowPrompt(true), 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    };
  }, []);

  useEffect(() => {
    if (showPrompt) {
      trackEvent('pwa_install_prompt', { platform: isIOS ? 'ios' : 'web' });
    }
  }, [isIOS, showPrompt]);

  const handleInstallClick = async () => {
    if (isIOS) {
      // Show iOS installation guide
      setShowIOSGuide(true);
      trackEvent('pwa_install_guide_open', { platform: 'ios' });
      return;
    }

    if (!deferredPrompt) {
      console.warn('[InstallPrompt] No deferred prompt available');
      return;
    }

    // Show native install prompt
    deferredPrompt.prompt();

    // Wait for user choice
    const { outcome } = await deferredPrompt.userChoice;
    console.log('[InstallPrompt] User choice:', outcome);

    if (outcome === 'accepted') {
      console.log('[InstallPrompt] User accepted install');
      trackEvent('pwa_install_accept', { platform: 'web' });
    } else {
      console.log('[InstallPrompt] User dismissed install');
      trackEvent('pwa_install_dismiss', { platform: 'web' });
    }

    // Clear prompt
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    console.log('[InstallPrompt] User dismissed prompt');
    localStorage.setItem('pwa_prompt_dismissed', Date.now().toString());
    trackEvent('pwa_install_dismiss', { platform: isIOS ? 'ios' : 'web' });
    setShowPrompt(false);
  };

  const handleCloseIOSGuide = () => {
    setShowIOSGuide(false);
    handleDismiss();
  };

  if (!showPrompt) {
    return null;
  }

  return (
    <>
      {/* Install Banner */}
      <div className="pwa-install-prompt">
        <div className="pwa-install-content">
          <div className="pwa-install-icon">
            <img src="/icon-512.png.svg" alt={t('settings.app.installTitle', { defaultValue: 'Install LOOPINO AI' })} />
          </div>

          <div className="pwa-install-text">
            <h3>{t('settings.app.installTitle', { defaultValue: 'Install LOOPINO AI' })}</h3>
            <p>{t('settings.app.installDescription', { defaultValue: 'Get faster access with the app installed' })}</p>
          </div>

          <div className="pwa-install-actions">
            <button
              className="pwa-install-btn pwa-install-btn-primary"
              onClick={handleInstallClick}
            >
              {t('settings.app.actions.install', { defaultValue: 'Install' })}
            </button>
            <button
              className="pwa-install-btn pwa-install-btn-secondary"
              onClick={handleDismiss}
            >
              {t('common.actions.later', { defaultValue: 'Later' })}
            </button>
          </div>
        </div>

        <button
          className="pwa-install-close"
          onClick={handleDismiss}
          aria-label={t('common.actions.close', { defaultValue: 'Close' })}
        >
          ✕
        </button>
      </div>

      {/* iOS Installation Guide Modal */}
      {showIOSGuide && (
        <div className="pwa-ios-guide-overlay" onClick={handleCloseIOSGuide}>
          <div className="pwa-ios-guide-modal" onClick={(e) => e.stopPropagation()}>
            <button className="pwa-ios-guide-close" onClick={handleCloseIOSGuide}>
              ✕
            </button>

            <h2>{t('settings.app.ios.title', { defaultValue: 'Install LOOPINO AI on iOS' })}</h2>

            <div className="pwa-ios-guide-steps">
              <div className="pwa-ios-guide-step">
                <div className="pwa-ios-guide-step-number">1</div>
                <div className="pwa-ios-guide-step-content">
                  <p>
                    <Trans
                      ns="mobile"
                      i18nKey="settings.app.ios.steps.share.main"
                      defaults="Tap the <strong>Share</strong> icon 📤"
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <p className="pwa-ios-guide-step-hint">{t('settings.app.ios.steps.share.hint', { defaultValue: '(bottom center on Safari)' })}</p>
                </div>
              </div>

              <div className="pwa-ios-guide-step">
                <div className="pwa-ios-guide-step-number">2</div>
                <div className="pwa-ios-guide-step-content">
                  <p>
                    <Trans
                      ns="mobile"
                      i18nKey="settings.app.ios.steps.addToHome.main"
                      defaults='Scroll and choose <strong>"Add to Home Screen"</strong>'
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <p className="pwa-ios-guide-step-hint">{t('settings.app.ios.steps.addToHome.hint', { defaultValue: '(icon with the + symbol)' })}</p>
                </div>
              </div>

              <div className="pwa-ios-guide-step">
                <div className="pwa-ios-guide-step-number">3</div>
                <div className="pwa-ios-guide-step-content">
                  <p>
                    <Trans
                      ns="mobile"
                      i18nKey="settings.app.ios.steps.confirm.main"
                      defaults='Tap <strong>"Add"</strong> in the top-right corner'
                      components={{ strong: <strong /> }}
                    />
                  </p>
                  <p className="pwa-ios-guide-step-hint">{t('settings.app.ios.steps.confirm.hint', { defaultValue: 'The icon will appear on your home screen' })}</p>
                </div>
              </div>
            </div>

            <div className="pwa-ios-guide-benefits">
              <h3>{t('pwa.iosGuide.benefitsTitle', { defaultValue: 'Benefits:' })}</h3>
              <ul>
                <li>{t('pwa.iosGuide.benefits.home', { defaultValue: '✓ Instant access from your home screen' })}</li>
                <li>{t('pwa.iosGuide.benefits.fullscreen', { defaultValue: '✓ Fullscreen experience without the browser bar' })}</li>
                <li>{t('pwa.iosGuide.benefits.offline', { defaultValue: '✓ Works offline too' })}</li>
              </ul>
            </div>

            <button
              className="pwa-ios-guide-btn"
              onClick={handleCloseIOSGuide}
            >
              {t('settings.app.ios.close', { defaultValue: 'Got it' })}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default InstallPrompt;
